'use client'

import React, { useEffect, FC, ReactElement, useRef } from 'react'
import { safeDecodeCollectionSlug } from '@/utils/collection-object'
import { createChart, ColorType } from 'lightweight-charts'
import type { LineData, BarData, HistogramData, Time } from 'lightweight-charts'
import { useTheme } from 'next-themes'
import { NETWORK } from '@/utils/constants'
import { formatSatsPer } from '@/utils/format-sats'
import isFungible from '@/utils/is-fungible'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import classNames from 'classnames'
import CircleLoading from '@/components/circle-loading'
import useWindowSize from '@/utils/hooks/useWindowSize'
import { useCollectionVWAPRange, VWAPInterval } from '../../../utils/hooks/useCollectionVWAP'
import useTokenPrices from '@/utils/hooks/useTokenPrices'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { getSatoshiPriceUSD } from '@/utils/hooks/useSatoshiPriceUSD'
import { formatUsdPerBillion } from '@/utils/format-usd'
import { CandlestickChart, LineChart as LineChartIcon } from 'lucide-react'
import PercentChange from '@/components/percent-change'
import CollectionIcon from '@/components/collection-icon'
import VerifiedBadge from '@/components/verified-badge'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

// Using daily VWAP buckets now; prior intraday grouping logic removed

// Outlier removal not necessary for pre-aggregated VWAP daily data

// const bucketTabs = ['5m', '1h', '4h', '1D'] // using daily buckets only
const scaleTabs = ['Linear', 'Log', 'Percent']

const rangeOptions: { label: string; days: number | 'ytd' | null }[] = [
	{ label: '1D', days: 1 },
	{ label: '1W', days: 7 },
	{ label: '1M', days: 30 },
	{ label: '3M', days: 90 },
	{ label: 'YTD', days: 'ytd' },
	{ label: '1Y', days: 365 },
	{ label: 'ALL', days: null }
]

// Bucket size per range. Hourly is used for the short windows (1D/1W); 1M and
// broader use daily candles to keep bar counts reasonable.
const RANGE_INTERVAL: Record<string, VWAPInterval> = {
	'1D': '1h',
	'1W': '1h',
	'1M': '1d',
	'3M': '1d',
	YTD: '1d',
	'1Y': '1d',
	ALL: '1d'
}

const SECONDS_PER_DAY = 24 * 60 * 60

// Cap "ALL" at what the server will serve in one contiguous request set.
// 6 × 5y = 30y of daily is plenty; no real collection comes close.
const ALL_MAX_DAYS = 6 * 5 * 365

const resolveRangeDays = (d: number | 'ytd' | null): number | null => {
	if (d === 'ytd') {
		const now = new Date()
		const soy = new Date(now.getFullYear(), 0, 1)
		return Math.ceil((now.getTime() - soy.getTime()) / (24 * 60 * 60 * 1000))
	}
	return d
}

const buildRangeQuery = (selectedRange: string) => {
	const nowMs = Date.now()
	const now = Math.floor(nowMs / 1000)
	const opt = rangeOptions.find((r) => r.label === selectedRange)
	const resolvedDays = opt
		? opt.days === 'ytd'
			? Math.ceil((nowMs - new Date(new Date(nowMs).getFullYear(), 0, 1).getTime()) / 86400000)
			: opt.days ?? ALL_MAX_DAYS
		: 7
	const span = (resolvedDays || 7) * SECONDS_PER_DAY
	return {
		interval: RANGE_INTERVAL[selectedRange] ?? '1d',
		start: now - span,
		end: now
	}
}

const CollectionChart: FC<{
	slug: string
	width: number
	height: number
	minimal?: boolean
	/** Render an internal price + range-% header above the canvas. Useful on
	 *  the main collection page; disable when the parent (e.g. modal body)
	 *  renders its own price block. */
	showPrice?: boolean
	/** Optional collection metadata used to render the icon + name row above
	 *  the price in the minimal header. */
	collection?: any
	onHoverPrice?: (label: string | null) => void
	onRangeChange?: (label: string, percentChange: number) => void
}> = (props): ReactElement => {
	const {
		slug,
		width,
		height,
		minimal = false,
		showPrice = false,
		collection,
		onHoverPrice,
		onRangeChange
	} = props
	const { isMobile } = useWindowSize()

	// Observe the chart wrapper so the lightweight-charts canvas always matches
	// the actual rendered container width (beats relying on a possibly-stale
	// `width` prop from an ancestor).
	const wrapperRef = useRef<HTMLDivElement>(null)
	const [measuredWidth, setMeasuredWidth] = React.useState(0)
	useEffect(() => {
		if (!wrapperRef.current) return
		const measure = () => {
			if (wrapperRef.current) setMeasuredWidth(wrapperRef.current.offsetWidth)
		}
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(wrapperRef.current)
		return () => observer.disconnect()
	}, [])
	// A parent can hand down a negative width while a side rail is mid-collapse
	// (container minus rails); lightweight-charts throws "Negative width is not
	// allowed for Size" on it and took the collection page down. Never below 0.
	const effectiveWidth = Math.max(measuredWidth, 0) || Math.max(width || 0, 0) || 300

	// Any render guards (invalid slug, load error, missing data) are evaluated
	// AFTER every hook below, so hook call order stays stable across renders
	// (React's rules-of-hooks requirement). Guard values here, branch at the
	// bottom of the component.
	const slugInvalid = !slug || typeof slug !== 'string'

	const chartWidth = effectiveWidth
	// In minimal mode we also render a price header (optional) and a tabs row
	// inside the wrapper; reserve that space out of the external height so the
	// whole block fits without overflowing.
	const MINIMAL_TABS_H = 36
	const MINIMAL_PRICE_H = showPrice ? (collection ? 136 : 76) : 0
	const chartHeight = minimal
		? Math.max((height || 200) - MINIMAL_TABS_H - MINIMAL_PRICE_H, 100)
		: height || 200

	// `isFungible('')` returns falsy so this is safe when slug is invalid; the
	// slugInvalid guard at the bottom of the component will short-circuit the
	// render before any consumer of `fungible` runs.
	const fungible = isFungible(slug || '')

	// const [selectedBucketTab, setSelectedBucketTab] = React.useState('1h')
	const [selectedScaleTab, setSelectedScaleTab] = React.useState(fungible ? 'Log' : 'Linear')
	const ref = useRef<HTMLDivElement>(null)
	// Per-collection default range. Bitmap defaults to 1M (1W is too noisy
	// on a high-volume collection), other non-fungible (NFT) collections to
	// 1W, and fungible tokens to YTD.
	const slugLower = (slug || '').toLowerCase()
	const defaultRange = slugLower === 'bitmap' ? '1M' : !fungible ? '1W' : 'YTD'
	const [selectedRange, setSelectedRange] = React.useState<string>(defaultRange)
	const [rangeQuery, setRangeQuery] = React.useState(() => buildRangeQuery(defaultRange))
	const handleSelectRange = React.useCallback((label: string) => {
		setSelectedRange(label)
		setRangeQuery(buildRangeQuery(label))
	}, [])

	// Map the currently selected range tab to (interval, window). The hook
	// splits the window into server-sized chunks and stitches them — every
	// chunk URL is independently cacheable at the CDN. Picking hourly for
	// ≤30d keeps the candle count well under 1k per request.
	const { interval, start, end } = rangeQuery
	const {
		data: vwapData,
		error,
		isLoading
	} = useCollectionVWAPRange(slug || '', interval, start, end)

	const [chartType, setChartType] = React.useState<'line' | 'candlestick'>('line')
	// Hover-only price; null when the cursor isn't over the chart so the
	// default display falls through to `externalPrice` (see below).
	const [hoveredValue, setHoveredValue] = React.useState<number | null>(null)
	// Hover-relative percent (delta from selected-range start to hovered point).
	// Kept in its own state so hover never mutates `rangePct` — that variable
	// drives `isDown`, which is a chart-effect dependency, so updating it on
	// hover would tear down and rebuild the chart on every move and the
	// displayed pct would stick on the at-rest baseline.
	const [hoveredPct, setHoveredPct] = React.useState<number | null>(null)
	const onHoverPriceRef = useRef(onHoverPrice)
	const onRangeChangeRef = useRef(onRangeChange)
	const lastRangeEmissionRef = useRef<{ label: string; value: number } | null>(null)

	useEffect(() => {
		onHoverPriceRef.current = onHoverPrice
	}, [onHoverPrice])

	useEffect(() => {
		onRangeChangeRef.current = onRangeChange
	}, [onRangeChange])

	const emitRangeChange = React.useCallback((label: string, value: number) => {
		const current = lastRangeEmissionRef.current
		if (current?.label === label && Math.abs(current.value - value) < 0.000001) return
		const next = { label, value }
		lastRangeEmissionRef.current = next
		onRangeChangeRef.current?.(label, value)
	}, [])

	// Live floor/last price from the same source the header uses, so the
	// chart's top-left price tracks real-time listings instead of sitting on
	// whatever the VWAP endpoint last reported (which can be many minutes
	// behind, especially if the SW cached it).
	const { data: tokenPrices } = useTokenPrices()
	const { exchangeRate } = useExchangeRate()
	const externalPrice = React.useMemo<number | null>(() => {
		try {
			const entry = (tokenPrices || []).find((e: any) => e.slug === safeDecodeCollectionSlug(slug))
			const p = entry?.floor_price_per ?? (collection as any)?.floor_price_per
			const n = typeof p === 'string' ? parseFloat(p) : typeof p === 'number' ? p : NaN
			return Number.isFinite(n) && n > 0 ? n : null
		} catch {
			return null
		}
	}, [tokenPrices, slug, collection])

	// Shared data pipeline used by both line and candlestick render modes.
	const lineData = React.useMemo(() => {
		if (!Array.isArray(vwapData)) return [] as { time: number; value: number }[]
		return (vwapData as any[])
			.flatMap((d) =>
				d && typeof d.time === 'number' && typeof d.vwap === 'number'
					? [{ time: d.time as number, value: fungible ? d.vwap * 1e8 : d.vwap }]
					: []
			)
			.sort((a, b) => a.time - b.time)
	}, [vwapData, fungible])

	const visibleInfo = React.useMemo(() => {
		if (lineData.length === 0) return { startIdx: 0, lastIdx: 0, rangePct: 0 }
		const rangeOpt = rangeOptions.find((r) => r.label === selectedRange)
		const resolvedDays = resolveRangeDays(rangeOpt?.days ?? null)
		const lastIdx = lineData.length - 1
		const lastTime = lineData[lastIdx].time
		let startIdx = 0
		if (resolvedDays) {
			const fromTime = lastTime - resolvedDays * 24 * 60 * 60
			const found = lineData.findIndex((p) => p.time >= fromTime)
			startIdx = found < 0 ? 0 : found
		}
		const startPt = lineData[startIdx]
		const endPt = lineData[lastIdx]
		const pct =
			startPt?.value && endPt?.value ? ((endPt.value - startPt.value) / startPt.value) * 100 : 0
		return {
			startIdx,
			lastIdx,
			rangePct: Number.isFinite(pct) ? pct : 0
		}
	}, [lineData, selectedRange])
	const rangePct = visibleInfo.rangePct
	const lastPrice = React.useMemo(() => {
		const last = lineData[lineData.length - 1]
		return last ? { value: last.value, time: last.time } : { value: 0, time: 0 }
	}, [lineData])
	const changePct = React.useMemo(() => {
		if (lineData.length < 2) return 0
		const a = lineData[lineData.length - 2].value
		const b = lineData[lineData.length - 1].value
		return a > 0 ? ((b - a) / a) * 100 : 0
	}, [lineData])

	// Keep rangePct + onRangeChange in sync with the selected range.
	// Only notify the parent once we have real data so the header doesn't flash
	// a placeholder "0.00%" before the VWAP response arrives.
	useEffect(() => {
		if (lineData.length > 1) {
			emitRangeChange(selectedRange, visibleInfo.rangePct)
		}
	}, [selectedRange, visibleInfo.rangePct, lineData.length, emitRangeChange])

	// Red when the selected range closed lower than it opened; green otherwise.
	// Keep the exact brand tokens used for candles so the UI reads as one system.
	// In light mode, swap to darker shades so the line pops against white.
	const { resolvedTheme } = useTheme()
	const isLight = resolvedTheme === 'light'
	const upColor = isLight ? '#039855' : '#6CE9A6'
	const downColor = isLight ? '#B42318' : '#F97066'
	const upRgb = isLight ? '3, 152, 85' : '108, 233, 166'
	const downRgb = isLight ? '180, 35, 24' : '249, 112, 102'
	const isDown = rangePct < 0
	const lineAccent = isDown ? downColor : upColor
	const gridColor = isLight ? 'rgba(0, 0, 0, .15)' : 'rgba(255, 255, 255, .2)'

	useEffect(() => {
		if (!vwapData || !effectiveWidth || !Array.isArray(vwapData)) return

		// VWAP endpoint returns daily buckets: { time: seconds, vwap: BTC price }
		// For fungible tokens, display in sats; for non-fungible, display in BTC
		const lineData: Array<LineData<Time>> = vwapData
			.flatMap((d) =>
				d && typeof d.time === 'number' && typeof d.vwap === 'number'
					? [{ time: d.time as Time, value: fungible ? d.vwap * 1e8 : d.vwap }]
					: []
			)
			.sort((a, b) => Number(a.time as any) - Number(b.time as any))

		const handleResize = () => {
			if (ref.current) {
				// @ts-ignore
				chart.applyOptions({ width: ref.current.clientWidth })
			}
		}

		if (!ref.current) {
			return
		}

		const chart = createChart(ref.current, {
			layout: {
				background: {
					type: ColorType.Solid,
					color: minimal ? 'rgba(0,0,0,0)' : isLight ? '#FFFFFF' : 'black'
				},
				textColor: isLight ? '#0F172A' : 'white',
				attributionLogo: !minimal
			},
			grid: {
				vertLines: {
					visible: false
				},
				horzLines: {
					visible: !minimal,
					color: gridColor
				}
			},
			rightPriceScale: {
				mode: scaleTabs.findIndex((e) => e === selectedScaleTab),
				autoScale: true, // Enable auto-scaling
				borderVisible: false,
				visible: !minimal
			},
			leftPriceScale: {
				visible: false,
				borderVisible: false
			},
			localization: {
				priceFormatter: (p: number) => {
					const { sats } = formatSatsPer(p)
					const coin = formatSatoshis(p * 1e8)
					return fungible ? `${sats} sats` : `${coin} ${NETWORK}`
				}
			},
			width: chartWidth,
			height: chartHeight,
			timeScale: {
				rightOffset: minimal ? 0 : isMobile ? 4 : 10,
				barSpacing: isMobile ? 6 : 10,
				fixLeftEdge: true,
				fixRightEdge: minimal,
				lockVisibleTimeRangeOnResize: true,
				rightBarStaysOnScroll: true,
				borderVisible: false,
				visible: !minimal,
				timeVisible: true,
				secondsVisible: false
			},
			handleScroll: {
				mouseWheel: true,
				pressedMouseMove: true,
				horzTouchDrag: true,
				vertTouchDrag: true
			},
			handleScale: {
				axisPressedMouseMove: true,
				mouseWheel: true,
				pinch: true
			}
		})

		// Series: area (line) or candlestick — candles are derived from daily
		// VWAP, using prev day's close as open so the body reflects day-over-
		// day change (flat wicks since we don't have intraday OHLC).
		const series =
			chartType === 'candlestick'
				? chart.addCandlestickSeries({
						upColor: upColor,
						downColor: downColor,
						borderUpColor: upColor,
						borderDownColor: downColor,
						wickUpColor: upColor,
						wickDownColor: downColor,
						priceLineVisible: false
				  })
				: chart.addAreaSeries({
						lineColor: lineAccent,
						topColor: isDown ? `rgba(${downRgb}, 0.18)` : `rgba(${upRgb}, 0.18)`,
						bottomColor: isDown ? `rgba(${downRgb}, 0.00)` : `rgba(${upRgb}, 0.00)`,
						lineWidth: 2,
						priceLineVisible: false,
						crosshairMarkerVisible: true,
						crosshairMarkerRadius: 3,
						crosshairMarkerBorderColor: lineAccent,
						crosshairMarkerBackgroundColor: isLight ? '#FFFFFF' : '#0B0B0B'
				  })

		if (chartType === 'candlestick') {
			const candleData = lineData.map((d, i) => {
				const prev = lineData[i - 1]?.value ?? d.value
				const openV = prev
				const closeV = d.value
				return {
					time: d.time,
					open: openV,
					high: Math.max(openV, closeV),
					low: Math.min(openV, closeV),
					close: closeV
				}
			})
			// @ts-ignore
			series.setData(candleData)
		} else {
			// @ts-ignore
			series.setData(lineData)
		}

		// Visible-range start drives hover-pct so the chip the user sees while
		// hovering is "(hovered − range start) / range start", consistent with
		// the resting baseline shown when the cursor leaves the canvas.
		let crosshairStartValue: number | null = null
		let crosshairBaselinePct = 0
		if (lineData.length > 0) {
			const rangeOpt = rangeOptions.find((r) => r.label === selectedRange)
			const resolvedDays = resolveRangeDays(rangeOpt?.days ?? null)
			const lastIdxLocal = lineData.length - 1
			const lastTimeLocal = Number(lineData[lastIdxLocal].time)
			let startIdxLocal = 0
			if (resolvedDays) {
				const fromTime = lastTimeLocal - resolvedDays * 24 * 60 * 60
				const found = lineData.findIndex((p) => Number(p.time) >= fromTime)
				startIdxLocal = found < 0 ? 0 : found
			}
			const sv = lineData[startIdxLocal]?.value
			const ev = lineData[lastIdxLocal]?.value
			if (typeof sv === 'number') crosshairStartValue = sv
			if (sv && ev && sv > 0) crosshairBaselinePct = ((ev - sv) / sv) * 100
		}

		chart.subscribeCrosshairMove((param) => {
			const point = param?.seriesData?.get?.(series) as
				| LineData<Time>
				| BarData<Time>
				| HistogramData<Time>
				| undefined

			// Line/area points carry `value`; candlestick points carry OHLC —
			// use `close` as the hovered price.
			let hoveredValue: number | undefined
			if (point) {
				if ('value' in point && point.value != null) hoveredValue = point.value
				else if ('close' in point && (point as BarData<Time>).close != null) {
					hoveredValue = (point as BarData<Time>).close
				}
			}

			if (typeof hoveredValue === 'number') {
				setHoveredValue(hoveredValue)
				if (typeof crosshairStartValue === 'number' && crosshairStartValue > 0) {
					const hoverPct = ((hoveredValue - crosshairStartValue) / crosshairStartValue) * 100
					setHoveredPct(hoverPct)
					if (lineData.length > 1) {
						emitRangeChange(selectedRange, hoverPct)
					}
				}
				const { sats: hoverSats } = formatSatsPer(hoveredValue)
				const label = fungible
					? `${hoverSats} sats`
					: `${formatSatoshis(hoveredValue * 1e8)} ${NETWORK}`
				onHoverPriceRef.current?.(label)
			} else {
				setHoveredValue(null)
				setHoveredPct(null)
				if (minimal && lineData.length > 1) {
					emitRangeChange(selectedRange, crosshairBaselinePct)
				}
				onHoverPriceRef.current?.(null)
			}
		})

		// Set the visible range. In minimal mode it's driven by the selected
		// range tab; otherwise fall back to the last ~120 daily points.
		const timeScale = chart.timeScale()
		if (minimal && lineData.length > 0) {
			const rangeOpt = rangeOptions.find((r) => r.label === selectedRange)
			const resolvedDays = resolveRangeDays(rangeOpt?.days ?? null)
			const lastIdx = lineData.length - 1
			const lastTime = Number(lineData[lastIdx].time)

			let startIdx = 0
			if (resolvedDays) {
				const fromTime = lastTime - resolvedDays * 24 * 60 * 60
				const found = lineData.findIndex((p) => Number(p.time) >= fromTime)
				startIdx = found < 0 ? 0 : found
			}

			// Explicitly stretch bars to fill the canvas — setVisibleLogicalRange
			// alone doesn't reliably recompute barSpacing in lightweight-charts
			// 4.1, so short ranges leave a gap on the right edge. A 1.05x
			// multiplier zooms content slightly beyond the canvas so the line
			// always reaches both edges even with subpixel rounding.
			const visibleBars = lastIdx - startIdx + 1
			if (visibleBars > 0 && chartWidth > 0) {
				timeScale.applyOptions({ barSpacing: (chartWidth / visibleBars) * 1.05 })
			}
			timeScale.setVisibleLogicalRange({
				from: startIdx - 0.5,
				to: lastIdx + 0.5
			})

			const startPoint = lineData[startIdx]
			const endPoint = lineData[lastIdx]
			const computedRangePct =
				startPoint?.value && endPoint?.value
					? ((endPoint.value - startPoint.value) / startPoint.value) * 100
					: 0
			const finalPct = Number.isFinite(computedRangePct) ? computedRangePct : 0
			if (lineData.length > 1) {
				emitRangeChange(selectedRange, finalPct)
			}
		} else {
			const numberOfPoints = 120
			if (lineData.length > numberOfPoints) {
				const lastDataIndex = lineData.length - 1
				const firstVisibleIndex = Math.max(0, lastDataIndex - numberOfPoints)
				timeScale.setVisibleRange({
					from: lineData[firstVisibleIndex].time,
					to: lineData[lastDataIndex].time
				})
			} else {
				timeScale.fitContent()
			}
		}

		window.addEventListener('resize', handleResize)

		// Belt-and-suspenders reset: subscribeCrosshairMove doesn't always fire
		// with empty seriesData when the cursor exits the chart on touch /
		// pointer-cancel paths, so the hover chip can stick on the last hovered
		// value. A native pointerleave on the container guarantees we revert
		// to the at-rest baseline (rangePct / changePct).
		const chartHost = ref.current
		const handleLeave = () => {
			setHoveredValue(null)
			setHoveredPct(null)
			if (minimal && lineData.length > 1) {
				emitRangeChange(selectedRange, crosshairBaselinePct)
			}
			onHoverPriceRef.current?.(null)
		}
		chartHost?.addEventListener('pointerleave', handleLeave)
		chartHost?.addEventListener('mouseleave', handleLeave)

		return () => {
			window.removeEventListener('resize', handleResize)
			chartHost?.removeEventListener('pointerleave', handleLeave)
			chartHost?.removeEventListener('mouseleave', handleLeave)
			chart.remove()
		}
	}, [
		selectedScaleTab,
		vwapData,
		fungible,
		height,
		effectiveWidth,
		isMobile,
		minimal,
		selectedRange,
		chartType,
		isDown,
		isLight,
		gridColor,
		chartWidth,
		chartHeight,
		upColor,
		downColor,
		lineAccent,
		downRgb,
		upRgb,
		emitRangeChange
	])

	const displayLastPrice = hoveredValue ?? lastPrice.value
	const { sats: displayLastPriceSats } = formatSatsPer(displayLastPrice)

	// const renderBucketTab = React.useCallback(
	// 	(e) => {
	// 		return (
	// 			<div
	// 				className={classNames(
	// 					'w-[60px] h-[28px] text-sm flex items-center justify-center font-semibold bg-chrome-muted cursor-pointer rounded-lg',
	// 					{ ['bg-success text-success-foreground']: selectedBucketTab === e }
	// 				)}
	// 				onClick={() => setSelectedBucketTab(e)}
	// 				key={e}
	// 			>
	// 				{e}
	// 			</div>
	// 		)
	// 	},
	// 	[selectedBucketTab]
	// )

	const _renderScaleTab = React.useCallback(
		(e: string) => {
			return (
				<div
					className={classNames(
						'w-[60px] h-[28px] flex items-center justify-center text-xs font-semibold bg-chrome-muted cursor-pointer rounded-lg',
						{ ['bg-success text-success-foreground']: selectedScaleTab === e }
					)}
					onClick={() => setSelectedScaleTab(e)}
					key={e}
					role="button"
					tabIndex={0}
					onKeyDown={onKeyboardClick(() => setSelectedScaleTab(e))}
				>
					{e}
				</div>
			)
		},
		[selectedScaleTab]
	)

	// Render guards — moved here so every hook above runs unconditionally on
	// every render (rules-of-hooks). Order matters only for message priority:
	// invalid slug wins over load error, which wins over the loading spinner.
	if (slugInvalid) {
		return (
			<div className="size-full flex flex-col items-center justify-center">
				<CircleLoading size={40} />
				<p className="mt-4 text-red-500">Error: Invalid chart data</p>
			</div>
		)
	}

	if (error) {
		return (
			<div className="size-full flex flex-col items-center justify-center">
				<CircleLoading size={40} />
				<p className="mt-4 text-red-500">Error: Unable to load chart data</p>
			</div>
		)
	}

	if (!vwapData) {
		return (
			<div className="size-full flex items-center justify-center">
				<CircleLoading size={40} />
			</div>
		)
	}

	if (minimal) {
		const hasChartData = Array.isArray(vwapData) && vwapData.length > 0
		const showLoader = isLoading && !hasChartData
		// 1Y / ALL ranges pull years of daily buckets — show an explainer alongside
		// the spinner while the (uncached) chunks are still streaming in so users
		// don't stare at an empty canvas wondering whether the chart silently failed.
		const showLongRangeMessage = showLoader && (selectedRange === '1Y' || selectedRange === 'ALL')
		return (
			<div ref={wrapperRef} className="relative flex w-full flex-col overflow-hidden">
				{showPrice && (
					<div className="px-5 pt-2 pb-3">
						{collection && (
							<div className="mb-3 flex items-center gap-3">
								<CollectionIcon
									className="size-10 shrink-0 overflow-hidden rounded-full"
									collection={collection}
								/>
								<h2 className="flex items-center gap-1 truncate text-base font-semibold text-foreground">
									<span className="truncate">{collection?.name}</span>
									<VerifiedBadge slug={slug} />
								</h2>
							</div>
						)}
						{(() => {
							// Hover wins; otherwise prefer the live tokenPrices value
							// so the chart matches the header's "Last Price" which
							// polls a different (faster) source than VWAP.
							const displayValue = hoveredValue ?? externalPrice ?? lastPrice.value
							const { sats: displaySats } = formatSatsPer(displayValue)
							// Convert the on-chart value to USD. Fungibles store sats
							// per token (so already in sats); NFTs store BTC, multiply
							// to sats first.
							const valueInSats = fungible ? displayValue : displayValue * 1e8
							const isNat = slug?.toUpperCase() === 'TAP-DMT-NAT'
							const usdLabel =
								valueInSats > 0
									? isNat
										? formatUsdPerBillion(valueInSats * 1e-8 * exchangeRate)
										: `$${getSatoshiPriceUSD(valueInSats, exchangeRate)}`
									: null
							return (
								<>
									<div className="text-[32px] font-semibold leading-none tracking-[-0.02em] text-foreground">
										{fungible ? displaySats : formatSatoshis(displayValue * 1e8)}
										<span className="ml-2 text-[18px] font-medium text-foreground/50">
											{fungible ? 'sats' : NETWORK}
										</span>
									</div>
									<div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm leading-none">
										{usdLabel && <span className="font-medium text-foreground/65">{usdLabel}</span>}
										<span className="inline-flex items-baseline gap-1">
											<PercentChange value={hoveredPct ?? rangePct} />
											<span className="text-muted-foreground">{selectedRange}</span>
										</span>
									</div>
								</>
							)
						})()}
					</div>
				)}
				<div className="relative w-full" style={{ height: chartHeight }}>
					{hasChartData && <div ref={ref} className="h-full" />}
					{showLoader && (
						<div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
							<CircleLoading size={32} />
							{showLongRangeMessage && (
								<p className="text-[12px] text-muted-foreground">
									Loading data, this could take some time.
								</p>
							)}
						</div>
					)}
					{!hasChartData && !showLoader && (
						<div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
							Chart unavailable
						</div>
					)}
				</div>
				<div className="mt-auto flex items-center gap-2 px-3 pt-1 pb-1">
					<div className="flex flex-1 items-center justify-around gap-1 sm:flex-none sm:justify-start sm:gap-3">
						{rangeOptions.map((opt) => (
							<button
								key={opt.label}
								type="button"
								onClick={() => handleSelectRange(opt.label)}
								className={classNames(
									'rounded-md px-1.5 py-0.5 text-xs font-bold tracking-wide transition-colors',
									selectedRange === opt.label
										? 'bg-success text-success-foreground'
										: 'text-success hover:text-foreground'
								)}
							>
								{opt.label}
							</button>
						))}
					</div>
					<button
						type="button"
						aria-label={chartType === 'candlestick' ? 'Show line chart' : 'Show candlestick chart'}
						onClick={() => setChartType((prev) => (prev === 'line' ? 'candlestick' : 'line'))}
						className="ml-auto flex size-7 flex-shrink-0 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted"
					>
						{chartType === 'candlestick' ? (
							<LineChartIcon className="size-4" strokeWidth={2.2} />
						) : (
							<CandlestickChart className="size-4" strokeWidth={2.2} />
						)}
					</button>
				</div>
			</div>
		)
	}

	return (
		<div className="relative overflow-hidden bg-gradient-to-b from-[#0B0B0B] to-[#0E0E10] shadow-lg">
			{/* Compact, Robinhood/WAGMI-style header */}
			<div className="absolute left-0 top-0 z-10 p-2 md:p-4 bg-background/50 backdrop-blur-sm border-b border-border w-full">
				<div className="flex items-end justify-between gap-2">
					<div className="flex flex-col">
						<p className="text-[10px] md:text-xs text-muted-foreground">Last Price</p>
						{/* // @ts-ignore */}
						<div className="flex items-center gap-2">
							<p className="text-lg md:text-2xl font-bold">
								{fungible ? displayLastPriceSats : formatSatoshis(displayLastPrice * 1e8)}{' '}
								{fungible ? 'sats' : NETWORK}
							</p>
							{(() => {
								const displayedPct = hoveredPct ?? changePct
								return (
									<span
										className={classNames(
											'text-[10px] md:text-xs font-semibold px-2 py-0.5 rounded-full',
											displayedPct >= 0
												? 'bg-success/10 text-success'
												: 'bg-destructive/10 text-destructive'
										)}
									>
										{displayedPct >= 0 ? '+' : ''}
										{displayedPct.toFixed(2)}%
									</span>
								)
							})()}
						</div>
					</div>
					{/* Interval pills - compact on mobile (daily only for now) */}
					{/**
	        <div className="flex gap-1 md:gap-2">
	          {bucketTabs.map((e) => (
	            <div
	              key={e}
	              className={classNames(
	                'h-6 px-2 text-[10px] md:text-xs flex items-center justify-center font-semibold rounded-sm cursor-pointer transition-colors border border-border bg-transparent hover:bg-chrome-muted',
	                selectedBucketTab === e && 'border-success text-success bg-success/10'
	              )}
	              onClick={() => setSelectedBucketTab(e)}
	            >
	              {e}
	            </div>
	          ))}
	        </div>
	        **/}
				</div>
				{/* Secondary controls in one compact row */}
				<div className="flex gap-1 md:gap-2 mt-2">
					{scaleTabs.map((e) => (
						<div
							key={e}
							className={classNames(
								'h-6 px-2 text-[10px] md:text-xs flex items-center justify-center font-semibold rounded-sm cursor-pointer transition-colors border border-border bg-transparent hover:bg-chrome-muted',
								selectedScaleTab === e && 'border-success text-success bg-success/10'
							)}
							onClick={() => setSelectedScaleTab(e)}
							role="button"
							tabIndex={0}
							onKeyDown={onKeyboardClick(() => setSelectedScaleTab(e))}
						>
							{e}
						</div>
					))}
				</div>
			</div>
			{/* Chart canvas */}
			<div ref={ref} className="mt-[84px] md:mt-[110px]" />
		</div>
	)
}

export default CollectionChart
